import { useMutation } from 'react-query';
import { Button, Pane, Paragraph, Heading, Label, Text, Icon, EyeOpenIcon, EyeOffIcon } from 'evergreen-ui';
import { InputField, Header, CountrySelect } from '../../components';
import linkRequest from '../../queries/link';
import { useDispatch, useSelector } from 'react-redux';
import { updateUser } from '../../store/authSlice';
import { Support } from '../../components';
import { Formik, Form } from 'formik';
import { useNavigate } from 'react-router-dom';
import { AccountLinkingIcon } from '../../components/icons';
import usePasswordVisibility from '../../hooks/passwordVisibility';
import styled from 'styled-components';
import { useCallback, useEffect, useMemo, useState } from 'react';

const StyledEye = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  width: 64px;
  height: 84px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LinkAccountPage = () => {
  const dispatch = useDispatch();
  const user = useSelector(state => state.authentication.user);
  const token = useSelector(state => state.authentication.token);
  const navigate = useNavigate();
  const [lockTime, setLockTime] = useState(5);
  const [counter, setCounter] = useState(0);

  useEffect(() => {
    if (counter > 0) {
      setTimeout(() => setCounter(counter - 1), 1000);
    }
  }, [counter]);

  const linkQuery = useMutation(linkRequest, {
    useErrorBoundary: true,
    onSuccess: async data => {
      if (!data) return;
      dispatch(updateUser(data));
      navigate('/app/accountLinkingSuccess');
    },
  });

  const [passwordVisibility, togglePasswordVisibility] = usePasswordVisibility();

  const register = useCallback(
    async (values, actions) => {
      try {
        await linkQuery.mutateAsync({
          email: values.email,
          password: values.password,
          country: values.country,
          token,
        });
      } catch (error) {
        console.log(error);
      } finally {
        actions.setSubmitting(false);
        setCounter(lockTime);
        setLockTime(lockTime * 2 < 300 ? lockTime * 2 : 300);
      }
    },
    [lockTime, linkQuery, token],
  );

  const validateForm = (values, ...other) => {
    const errors = {};
    if (!values.email) {
      errors.email = 'Required';
    }
    if (!values.password) {
      errors.password = 'Required';
    }
    return errors;
  };

  const timeout = useMemo(() => counter > 0, [counter]);

  return (
    <Pane>
      <Pane paddingY={50}>
        <Header logoSize="170" subTitle={user?.email} />
      </Pane>
      <Pane display="flex" flexDirection="column" justifyContent="center" height="100%" padding={16} paddingTop={0}>
        <Pane>
          <Heading color="#EDEEF2" size={700}>
            Welcome,
          </Heading>
          <Paragraph paddingTop={16} paddingBottom={32}>
            <Text color="#636465">Login into your JE account to connect your account to our system.</Text>
          </Paragraph>
          <Pane textAlign="center">
            <AccountLinkingIcon />
          </Pane>
          <Heading color="#EDEEF2" size={700} marginTop={30}>
            Link Accounts
          </Heading>
        </Pane>
        <Formik
          initialValues={{ email: '', password: '', country: user.country || 'UK' }}
          validate={validateForm}
          onSubmit={register}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <Form onSubmit={handleSubmit}>
              <Label htmlFor="email" color="#636465">
                Provide account email
              </Label>{' '}
              <InputField
                type="email"
                name="email"
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                isInvalid={touched.email && !!errors.email}
                validationMessage={errors.email}
                placeholder="Enter Account Email Address"
                marginTop={-5}
                autocapitalize="none"
              />
              <Label htmlFor="password" color="#636465">
                Provide account password
              </Label>
              <div style={{ position: 'relative' }}>
                <InputField
                  type={passwordVisibility ? 'text' : 'password'}
                  name="password"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.password}
                  isInvalid={touched.password && !!errors.password}
                  validationMessage={errors.password}
                  placeholder="Enter Account Password"
                  marginTop={-5}
                  autocapitalize="none"
                />
                <StyledEye>
                  {passwordVisibility ? (
                    <Icon
                      icon={EyeOpenIcon}
                      color="#65696C"
                      size={24}
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    />
                  ) : (
                    <Icon
                      icon={EyeOffIcon}
                      color="#65696C"
                      size={24}
                      onClick={togglePasswordVisibility}
                      style={{ cursor: 'pointer' }}
                    />
                  )}
                </StyledEye>
              </div>
              <Label htmlFor="country" color="#636465">
                Country
              </Label>
              <CountrySelect name="country" />
              <Button
                type="submit"
                appearance="primary"
                marginTop="30px"
                height={58}
                width="100%"
                backgroundColor="#e78850"
                border="none"
                borderRadius={10}
                disabled={isSubmitting || timeout}
              >
                <Text color="#fcfdfd" fontWeight="600" fontSize={15} letterSpacing={2}>
                  {timeout ? 'wait ' + counter + 's' : 'CONFIRM'}
                </Text>
              </Button>
            </Form>
          )}
        </Formik>
      </Pane>
      <Support />
    </Pane>
  );
};

export default LinkAccountPage;
